import { useState, useCallback } from 'react';

/**
 * Custom hook for AI-powered movie recommendations using Gemini API
 * @param {Function} showSnackbar - Function to show notifications
 * @returns {Object} Recommendations state and methods
 */
const useRecommendations = (showSnackbar) => {
  const [showRecommendationsModal, setShowRecommendationsModal] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [isGeneratingRecommendations, setIsGeneratingRecommendations] = useState(false);
  const [recommendationError, setRecommendationError] = useState('');

  /**
   * Generate AI recommendations based on user's library
   * @param {Array} movies - Filtered library movies
   * @param {Object} activeCategory - Current active category
   */
  const handleGetRecommendations = useCallback(async (movies, activeCategory) => {
    if (movies.length === 0) {
      showSnackbar(
        'Add some movies to your library first to get recommendations!',
        'warning'
      );
      return;
    }

    setIsGeneratingRecommendations(true);
    setRecommendationError('');
    setRecommendations([]);
    setShowRecommendationsModal(true);

    const moviesForPrompt = movies
      .slice(0, 5)
      .map((movie) => `${movie.title} (${movie.genres.join(', ')})`)
      .join(', ');

    const prompt = `Given the following movies from my library, categorized as "${
      activeCategory?.name || 'All Movies'
    }": ${moviesForPrompt}. Suggest 3-5 similar movies that I might enjoy. For each suggestion, provide the movie title, year, and a brief one-sentence reason for the recommendation. Format your response as a JSON array of objects, with each object having 'title', 'year', and 'reason' keys. Ensure the suggestions are distinct from the movies provided.`;

    const chatHistory = [{ role: 'user', parts: [{ text: prompt }] }];
    const payload = {
      contents: chatHistory,
      generationConfig: {
        responseMimeType: 'application/json',
        responseSchema: {
          type: 'ARRAY',
          items: {
            type: 'OBJECT',
            properties: {
              title: { type: 'STRING' },
              year: { type: 'STRING' },
              reason: { type: 'STRING' },
            },
            propertyOrdering: ['title', 'year', 'reason'],
          },
        },
      },
    };

    const geminiApiKey = ''; // Canvas will automatically provide this if empty
    const apiUrl = `https://generativelanguage.googleapis.com/v1beta/models/gemini-2.0-flash:generateContent?key=${geminiApiKey}`;

    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          `API Error: ${response.status} ${response.statusText} - ${
            errorData.error?.message || 'Unknown error'
          }`
        );
      }

      const result = await response.json();

      if (
        result.candidates &&
        result.candidates.length > 0 &&
        result.candidates[0].content &&
        result.candidates[0].content.parts &&
        result.candidates[0].content.parts.length > 0
      ) {
        const json = result.candidates[0].content.parts[0].text;
        const parsedJson = JSON.parse(json);
        setRecommendations(parsedJson);
      } else {
        setRecommendationError('Failed to get recommendations. Please try again.');
      }
    } catch (error) {
      console.error('Error generating recommendations:', error);
      setRecommendationError(`Error generating recommendations: ${error.message}.`);
    } finally {
      setIsGeneratingRecommendations(false);
    }
  }, [showSnackbar]);

  /**
   * Close the recommendations modal
   */
  const closeRecommendationsModal = useCallback(() => {
    setShowRecommendationsModal(false);
  }, []);

  return {
    // State
    showRecommendationsModal,
    recommendations,
    isGeneratingRecommendations,
    recommendationError,

    // Actions
    handleGetRecommendations,
    closeRecommendationsModal,
  };
};

export default useRecommendations;
